import React, { useEffect, useState } from 'react';
import { Ban, CheckCircle, Filter, Loader2, RefreshCw, RotateCcw, Tag, Undo2 } from 'lucide-react';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import PaginationControls from '../../../../components/Shared/PaginationControls';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast';
import {
  listExternalCategoryMappings,
  updateExternalCategoryMapping,
} from '../../../../services/AdminApis';

const STATE_BADGE = {
  UNVERIFIED: 'bg-gray-100 text-gray-700',
  VERIFIED: 'bg-green-100 text-green-800',
  BLACKLISTED: 'bg-red-100 text-red-800',
};

const STATE_OPTIONS = [
  { value: 'UNVERIFIED', label: 'Unverified' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'BLACKLISTED', label: 'Blacklisted' },
  { value: 'ALL', label: 'All' },
];

const EMPTY_FILTERS = { state: 'UNVERIFIED', masterCategory: null, q: '' };

const FilterBadge = ({ label, onClear }) => (
  <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 cursor-default">
    {label}
    <span onClick={onClear} className="ml-1 cursor-pointer hover:text-red-500 font-bold">
      ×
    </span>
  </Badge>
);

export default function ExternalCategoryMappings() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [isFiltered, setIsFiltered] = useState(false);
  const [stagedFilters, setStagedFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterVersion, setFilterVersion] = useState(0);

  // Verify / remap dialog
  const [editRow, setEditRow] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Blacklist confirmation
  const [blacklistRow, setBlacklistRow] = useState(null);

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await listExternalCategoryMappings({
        page: pagination.page,
        limit: pagination.limit,
        verified: appliedFilters.state,
        master_category_id: appliedFilters.masterCategory?.id,
        q: appliedFilters.q?.trim() ? appliedFilters.q.trim() : undefined,
      });
      setRows(data?.items || []);
      setPagination((prev) => ({ ...prev, total: data?.total || 0 }));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load category mappings.';
      toast({ variant: 'destructive', title: 'Unable to load mappings', description: detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters(stagedFilters);
    setIsFiltered(true);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setStagedFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setIsFiltered(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilterVersion((current) => current + 1);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const applyUpdate = (updated) => {
    const stillMatches = appliedFilters.state === 'ALL' || updated.state === appliedFilters.state;
    if (stillMatches) {
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } else {
      setRows((prev) => prev.filter((r) => r.id !== updated.id));
      setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    }
  };

  const submitMapping = async (mappingId, payload, successMessage) => {
    setSubmitting(true);
    try {
      const updated = await updateExternalCategoryMapping(mappingId, payload);
      applyUpdate(updated);
      toast({ title: 'Mapping updated', description: successMessage });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update the mapping.';
      toast({ variant: 'destructive', title: 'Not updated', description: detail });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditCategory(null);
  };

  const closeEdit = () => {
    setEditRow(null);
    setEditCategory(null);
  };

  const handleSubmitEdit = async () => {
    if (!editRow) return;
    const masterCategoryId = editCategory?.id ?? editRow.master_category_id;
    if (!masterCategoryId) {
      toast({
        variant: 'destructive',
        title: 'Category required',
        description: 'Pick the master category this external name should map to.',
      });
      return;
    }
    const ok = await submitMapping(
      editRow.id,
      { master_category_id: masterCategoryId, is_verified: true },
      `"${editRow.external_name}" now maps to ${editCategory?.name || editRow.master_category_name}.`
    );
    if (ok) closeEdit();
  };

  const handleUnverify = (row) =>
    submitMapping(
      row.id,
      { master_category_id: row.master_category_id, is_verified: false },
      `"${row.external_name}" is inert again and no longer affects scrapes.`
    );

  const handleBlacklistConfirm = async () => {
    if (!blacklistRow) return;
    const ok = await submitMapping(
      blacklistRow.id,
      { master_category_id: null, is_verified: true },
      `"${blacklistRow.external_name}" will now be dropped from future scrapes.`
    );
    if (ok) setBlacklistRow(null);
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">External Category Mappings</h1>
          <p className="text-sm text-gray-500 mt-1">
            How Google's own category names map to ours. Mappings point at{' '}
            <strong>master categories</strong> — the scraper resolves each one down to the scraping
            task's own country. Unverified rows are inert suggestions; only verified mappings affect
            what the scraper does.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={loadRows} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500">State</Label>
              <Select
                value={stagedFilters.state}
                onValueChange={(value) => setStagedFilters((prev) => ({ ...prev, state: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[240px]">
              <Label className="text-xs text-gray-500">Master category</Label>
              <div className="mt-1">
                {/* locale="master" makes /admin/common/basic-list return MasterCategory
                    rows, so this is a master picker with no new component. */}
                <CategorySearch
                  key={`filter-master-${filterVersion}`}
                  locale="master"
                  onSelect={(masterCategory) =>
                    setStagedFilters((prev) => ({ ...prev, masterCategory }))
                  }
                  typeFilter="child"
                  placeholder={stagedFilters.masterCategory?.name || 'Filter by master category...'}
                />
              </div>
            </div>

            <div className="w-[220px]">
              <Label className="text-xs text-gray-500">External name</Label>
              <Input
                className="mt-1"
                value={stagedFilters.q}
                onChange={(event) => setStagedFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="Search Google's name..."
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={handleApplyFilters}>
                <Filter size={16} className="mr-2" />
                Apply
              </Button>
              {isFiltered && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResetFilters}
                  className="text-red-600 hover:bg-red-50"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {isFiltered && (
            <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t">
              <span className="text-xs font-semibold text-gray-500">Filtered by:</span>
              <FilterBadge
                label={`State: ${STATE_OPTIONS.find((o) => o.value === appliedFilters.state)?.label}`}
                onClear={() => {
                  setStagedFilters((p) => ({ ...p, state: 'UNVERIFIED' }));
                  setAppliedFilters((p) => ({ ...p, state: 'UNVERIFIED' }));
                }}
              />
              {appliedFilters.masterCategory && (
                <FilterBadge
                  label={`Master: ${appliedFilters.masterCategory.name}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, masterCategory: null }));
                    setAppliedFilters((p) => ({ ...p, masterCategory: null }));
                  }}
                />
              )}
              {appliedFilters.q?.trim() && (
                <FilterBadge
                  label={`Name: ${appliedFilters.q.trim()}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, q: '' }));
                    setAppliedFilters((p) => ({ ...p, q: '' }));
                  }}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mappings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="min-w-[220px]">External name</TableHead>
                  <TableHead>Maps to (master)</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Used by</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-gray-500 py-8">
                      {loading ? 'Loading...' : 'No mappings match these filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.external_name}</TableCell>
                    <TableCell>
                      {row.state === 'BLACKLISTED' ? (
                        <Badge className="bg-red-100 text-red-800">Drops all matches</Badge>
                      ) : (
                        <span className="text-sm">{row.master_category_name || '—'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-gray-100 text-gray-700">
                        {row.source === 'GOOGLE_ORGANIC' ? 'Search' : 'Maps'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.usage_count} {row.usage_count === 1 ? 'business' : 'businesses'}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATE_BADGE[row.state] || 'bg-gray-100 text-gray-700'}>
                        {row.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:bg-green-50 hover:text-green-700"
                          onClick={() => openEdit(row)}
                          title={row.state === 'VERIFIED' ? 'Remap' : 'Verify or remap'}
                        >
                          {row.state === 'VERIFIED' ? (
                            <Tag className="w-4 h-4" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </Button>
                        {row.state !== 'BLACKLISTED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setBlacklistRow(row)}
                            title="Blacklist"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                        {row.state !== 'UNVERIFIED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-600 hover:bg-gray-50"
                            onClick={() => handleUnverify(row)}
                            disabled={submitting}
                            title="Un-verify"
                          >
                            <Undo2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {rows.length > 0 && (
            <div className="pt-4 flex justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                Showing {rows.length} of {pagination.total} mappings
              </p>
              <PaginationControls
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(editRow)} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Verify mapping</DialogTitle>
            <DialogDescription>
              Once verified, every future business Google labels "{editRow?.external_name}" is filed
              under this master category — resolved to each scraping task's own country, and
              overriding whatever category that task was for.
            </DialogDescription>
          </DialogHeader>

          {editRow && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p>
                  Google's name: <span className="font-medium">{editRow.external_name}</span>
                </p>
                <p className="text-gray-600">
                  Currently suggested: {editRow.master_category_name || 'none'}
                </p>
                <p className="text-gray-600">
                  Affects {editRow.usage_count}{' '}
                  {editRow.usage_count === 1 ? 'discovered business' : 'discovered businesses'} so far.
                </p>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Map to master category</Label>
                <div className="mt-1">
                  <CategorySearch
                    key={`edit-master-${editRow.id}`}
                    locale="master"
                    onSelect={setEditCategory}
                    typeFilter="child"
                    placeholder="Search master category..."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {editCategory
                    ? 'Replaces the current suggestion.'
                    : editRow.master_category_id
                      ? 'Leave blank to verify the existing suggestion as-is.'
                      : 'Pick a master category — this mapping has no suggestion to fall back on.'}
                </p>
              </div>

              {/* Saving with nothing picked falls back to the existing suggestion, which is
                  valid but looks like the form was ignored. State the outcome outright. */}
              <div className="rounded-md border p-3 text-sm">
                {editCategory ? (
                  <p>
                    Will verify as:{' '}
                    <span className="font-medium">{editCategory.name}</span>{' '}
                    <span className="text-gray-500">(ID: {editCategory.id})</span>
                  </p>
                ) : editRow.master_category_id ? (
                  <p>
                    Will verify as:{' '}
                    <span className="font-medium">{editRow.master_category_name}</span>{' '}
                    <span className="text-gray-500">
                      (ID: {editRow.master_category_id}, the scraper's existing suggestion)
                    </span>
                  </p>
                ) : (
                  <p className="text-red-600">
                    Nothing to verify — pick a master category above.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeEdit} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmitEdit}
              disabled={submitting || !(editCategory?.id || editRow?.master_category_id)}
            >
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              Verify mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(blacklistRow)} onOpenChange={(open) => !open && setBlacklistRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Blacklist "{blacklistRow?.external_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Every future business Google labels "{blacklistRow?.external_name}" will be dropped
              during scraping, across <strong>all tasks and all categories</strong> — silently, with
              no record beyond a counter in the scraper log.
              <br />
              <br />
              This external name has matched <strong>{blacklistRow?.usage_count}</strong>{' '}
              {blacklistRow?.usage_count === 1 ? 'business' : 'businesses'} so far. Already-discovered
              rows are not deleted; only future scrapes are affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleBlacklistConfirm();
              }}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              Blacklist
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
